
































































































































































































import { PropType } from 'vue';
import { AxiosError } from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { get } from 'vuex-pathify';

import { requiredValidator } from '@/utils/validators';
import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import { diffDeep } from '@/utils/helpers';

import { ALForm } from '@/common/component.types';
import {
  CurrencyRate,
  IQuote,
  IQuoteCustomer,
  IQuotePackage,
  IQuotePaymentTermItem,
  QuotePackage,
  QuoteType
} from '@/models';
import { FieldErrors, SystemError } from '@/models/error/error';
import { ISystemError, ISystemErrorData } from '@/models/error/error.types';
import auth from '@/mixins/auth';

import QuotePicker from '@/components/quote-common/QuotePicker.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import QuoteLayoutMixin from '@/mixins/quotes/QuoteLayoutMixin';
import QuoteExpiredLabel from '../quote-common/QuoteExpiredLabel.vue';

const baseMixins = mixins(auth, Functions, QuoteLayoutMixin);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    quotePackage: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteDocumentPackageForm',

    components: {
      ConfirmationDialog,
      QuoteExpiredLabel,
      QuotePicker
    },

    props: {
      value: Object as PropType<IQuotePackage>,
      quote: Object as PropType<IQuote>,
      loading: Boolean
    },

    data() {
      return {
        QuoteType,
        lazyValue: this.value as null | IQuotePackage,

        formValid: false,

        selectedQuote: null as IQuote | null,
        error: null as ISystemError | null,

        dialog: false,
        differences: {} as {
          currency?: number;
          payment_terms?: IQuotePaymentTermItem[];
          validity_start_date?: string | Date;
          customer?: IQuoteCustomer;
        },

        resolvePromise: null as
          | null
          | ((value: boolean | PromiseLike<boolean>) => void)
      };
    },

    computed: {
      ...mapGetters({
        getCurrencyById: 'quote/getCurrencyById',
        getQuotePackageById: 'quote/getQuotePackageById',
        getQuotePackageByQuoteNumberAndRevision:
          'quote/getQuotePackageByQuoteNumberAndRevision',
        getQuotePackageLastAdded: 'quote/getQuotePackageLastAdded'
      }),

      getCurrencyById: get<(id: number) => CurrencyRate>(
        'currency/getCurrencyById'
      ),

      isLoading: {
        get(): boolean {
          return this.loading;
        },
        set(value: boolean) {
          this.$emit('update:loading', value);
        }
      },

      internalValue: {
        get(): IQuotePackage | null {
          return this.lazyValue;
        },
        set(val: IQuotePackage | null) {
          this.lazyValue = val;
          this.$emit('input', val);
        }
      },

      customAttrs(): object {
        return this.$attrs;
      },

      quoteHasDifferences(): boolean {
        return Object.keys(this.differences).length > 0;
      }
    },

    watch: {
      internalValue: {
        handler(v: IQuotePackage) {
          if (v && v.id) {
            if (!this.selectedQuote) {
              // finds other selected quote used
              const foundOtherQuote = v.quotes!.find(
                quote => quote.id != this.quote.id
              );
              this.selectedQuote = foundOtherQuote ?? null;
            }
          }
        },
        immediate: true
      },

      selectedQuote: {
        handler(value) {
          this.checkQuoteDifferences();
        },
        immediate: true
      }
    },

    methods: {
      requiredValidator,

      ...mapActions({
        setQuotePackageQuotes: 'quote/setQuotePackageQuotes',
        createQuotePackage: 'quote/createQuotePackage',
        setQuoteQuotePackage: 'quote/setQuoteQuotePackage'
      }),

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.quotePackage) {
          valid = this.$refs.quotePackage.validate();
        }
        return valid;
      },

      async saveQuotePackage() {
        this.clearError();
        if (this.validateForm()) {
          let quotePackageInstance: IQuotePackage | null = null;

          try {
            quotePackageInstance = new QuotePackage(this.internalValue!);
          } catch (error) {
            if (SystemError.isSystemError(error)) {
              this.error = error;
            }
          }

          if (quotePackageInstance != null) {
            if (this.quoteHasDifferences) {
              const continueQuoteBuilder = await this.openDialog();

              if (!continueQuoteBuilder) return;
            }
            try {
              this.isLoading = true;
              // only if not editing then we create a new package
              if (!quotePackageInstance.id) {
                await this.createQuotePackage(quotePackageInstance);
                quotePackageInstance = this.getQuotePackageLastAdded;
              }

              // add this quote to package or leave it of it is already in the list
              const isSelectedQuoteInPackage = !!quotePackageInstance!.quotes!.find(
                quote => quote.id == this.selectedQuote?.id
              );

              if (!isSelectedQuoteInPackage) {
                const quotesList = [this.quote.id];
                if (this.selectedQuote) {
                  quotesList.push(this.selectedQuote.id);
                }
                await this.setQuotePackageQuotes({
                  id: quotePackageInstance!.id,
                  quotes: quotesList
                });
                quotePackageInstance = this.getQuotePackageByQuoteNumberAndRevision(
                  this.quote.number,
                  this.quote.revision
                );

                //BUG Fix - this is needed to update the quote state package key once a quote package is created
                this.setQuoteQuotePackage(quotePackageInstance);
              }

              // returns created or update quote package
              this.$emit('save-quote-package', quotePackageInstance);
            } catch (e) {
              console.error(e);
              this.error = this.setErrorFromAxiosError(e as AxiosError);
            } finally {
              this.isLoading = false;
            }
          }
        }
      },

      checkQuoteDifferences() {
        if (!this.selectedQuote) {
          this.differences = {};
          return;
        }
        const baseQuoteComparisonObj = {
          currency: this.quote.currency,
          payment_terms: this.quote.payment_terms,
          validity_start_date: this.quote.validity_start_date,
          customer: this.quote.customer
        };

        const selectedQuoteComparisonObj = {
          currency: this.selectedQuote!.currency,
          payment_terms: this.selectedQuote!.payment_terms,
          validity_start_date: this.selectedQuote!.validity_start_date,
          customer: this.selectedQuote!.customer
        };

        this.differences = diffDeep(
          baseQuoteComparisonObj,
          selectedQuoteComparisonObj
        );
      },

      openDialog() {
        this.dialog = true; // Open the dialog
        // Return a promise that resolves based on button clicks
        return new Promise<boolean>((resolve, reject) => {
          this.resolvePromise = resolve;
        });
      },

      confirm() {
        this.dialog = false;
        this.resolvePromise!(true);
      },

      cancel() {
        this.dialog = false;
        this.resolvePromise!(false);
      },

      setErrorFromAxiosError(error: AxiosError) {
        const systemErrorData: ISystemErrorData = {
          name: error.name,
          message: error.message
        };
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          systemErrorData.message = error.response.statusText;
          if (error.response.data.detail) {
            systemErrorData.detail = error.response.data.detail;
          } else if (FieldErrors.isFieldError(error.response.data)) {
            systemErrorData.fieldErrors = new FieldErrors(error.response.data);
          }
        } else if (error.request) {
          // The request was made but no response was received (Can happen on 500 error)
          systemErrorData.detail =
            'There was an error in processing your request. Please check with IT Admin';
        }
        return new SystemError(systemErrorData);
      },

      clearError() {
        this.error = null;
      }
    }
  });
